import type { FilterSchema } from '@/shared/components/filter/validation'
import type { ChartConfig } from '@/shared/components/ui/chart'
import type { BIResponse } from '@/shared/generated'
import type { RenderableText } from 'recharts'
import type { WidgetPeriod, WidgetPeriods } from '../types'

import { useEffect, useState } from 'react'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { filter, inRange, map, pipe, prop, sum } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	XAxis,
	YAxis,
} from 'recharts'

import { useBIAnalyticQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from '@/shared/components/ui/chart'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { MainDashboardFilter } from '../../Filter'
import { SinglePeriodSelect } from '../../Periods'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'
import WidgetLoadingState from '../../WidgetLoadingState'

/**
 * Recharts (shadcn charts) port of {@link ./Widget} (Highcharts `column`) for
 * the charting-library migration spike (PCT-976): a single column series over
 * fixed price-change buckets, per-point colors via `Cell` (the `0` bucket is
 * neutral), rounded top corners, percent data labels above each column, and a
 * shadcn tooltip showing the bucket's revenue total. Kept alongside the
 * Highcharts version so both can be compared.
 */

const ranges = [
	['-35', [-1000, -35.1]],
	['-35-10', [-35, -10.1]],
	['-10-5', [-10, -5.1]],
	['-5-0', [-5, 0]],
	['0', [0, 0.1]],
	['0-5', [0.1, 5]],
	['5-10', [5.1, 10]],
	['10-35', [10.1, 35]],
	['35', [35.1, 1000]],
]
const categories = [
	'<-35%',
	'-35-10%',
	'-10-5%',
	'-5-0%',
	'0',
	'0-5%',
	'5-10%',
	'10-35%',
	'>35%',
]
const NEUTRAL_BUCKET_INDEX = categories.indexOf('0')
const brandColor = 'var(--primary)'
const neutralColor = 'var(--color-slate-500)'
const getSum = pipe([map(prop('1')), sum])
const getPercent = (num: number, total: number) => (num / total) * 100
const calculateRanges = (res: BIResponse | null): [number, number][] => {
	const { data } = res!
	const total = getSum(data)
	const getDataInRange = (isInRange: (a: number, b: number) => boolean) =>
		filter(pipe([prop('0'), isInRange]))(data)

	return ranges.map(prop('1')).map(([start, end]: [number, number]) => {
		const sum = getSum(getDataInRange(inRange(start, end)))

		return [sum, getPercent(sum, total)]
	})
}

export default function PriceChangeSegmentedWidget3({
	commonFilters,
	commonPeriods,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const [period, setPeriod] = useState(commonPeriods.main)
	useEffect(() => {
		setPeriod(commonPeriods.main)
	}, [commonPeriods])
	const defaultPeriod = commonPeriods.main
	const handleSetPeriod = (period: WidgetPeriod) => {
		setPeriod(period)
	}
	const filters = [...commonFilters, ...(params.filters ?? [])]
	const currency = useSelectedCurrency()
	const priceChangeQuery = useBIAnalyticQuery<[number, number][]>(
		{
			currency,
			label: 'PriceChangeSegmented',
			...period,
			metrics: ['revenue'],
			dimensions: ['diff_esp_pp_int'],
			filters: [
				...filters,
				{ name: 'optimization_group', operation: 'is_not_empty', value: 1 },
				{ name: 'optimization_status', operation: 'in', value: ['locked'] },
			],
			date_aggregation: null,
		},
		{ select: calculateRanges }
	)
	const chartData =
		priceChangeQuery?.data?.map(([extra, y], index) => ({
			y,
			extra,
			category: categories[index],
		})) || []
	const chartConfig = {
		y: {
			label: t('metric.revenue'),
			color: brandColor,
		},
	} satisfies ChartConfig

	return (
		<Widget className='relative flex-auto'>
			<WidgetHeader
				title={t('widget.price_change_segmented.title')}
				tooltip={t('widget.price_change_segmented.tooltip')}
			>
				<div className='flex items-center gap-2'>
					<MainDashboardFilter
						irremovableCount={commonFilters.length}
						filters={filters ?? []}
						onChange={(filters) => {
							onChangeParams({ ...params, filters })
						}}
					/>
					<SinglePeriodSelect
						period={period}
						defaultPeriod={defaultPeriod}
						onChangePeriod={handleSetPeriod}
						maxDate={new Date()}
					/>
				</div>
			</WidgetHeader>
			<WidgetLoadingState isTinted={priceChangeQuery.isLoading} />
			<WidgetErrorState isTinted={priceChangeQuery.isError} />
			<WidgetContent>
				<ChartContainer config={chartConfig} className='h-54 w-full'>
					<BarChart
						accessibilityLayer={false}
						data={chartData}
						margin={{ top: 16 }}
					>
						<CartesianGrid vertical={false} strokeDasharray='3 3' />
						<XAxis
							dataKey='category'
							tickLine={false}
							axisLine={false}
							fontSize={11}
							interval={0}
						/>
						<YAxis tickLine={false} axisLine={false} fontSize={10} />
						<ChartTooltip
							content={
								<ChartTooltipContent
									formatter={(_value, _name, item) => (
										<div className='flex w-full items-center gap-2'>
											<span className='text-background/50'>
												{t('app.total', { defaultValue: 'Total' })}
											</span>
											<span className='ml-auto font-mono font-semibold text-background tabular-nums'>
												{formatNumber(
													(item.payload as { extra: number }).extra,
													NumberFormats.Integer
												)}
											</span>
										</div>
									)}
								/>
							}
						/>
						<Bar dataKey='y' radius={[5, 5, 0, 0]}>
							{chartData.map((entry, index) => (
								<Cell
									key={entry.category}
									fill={
										index === NEUTRAL_BUCKET_INDEX ? neutralColor : brandColor
									}
								/>
							))}
							<LabelList
								dataKey='y'
								position='top'
								fontSize={11}
								fill='var(--color-foreground)'
								formatter={(value: RenderableText) =>
									typeof value !== 'number' ? '' : `${Math.round(value)}%`
								}
							/>
						</Bar>
					</BarChart>
				</ChartContainer>
			</WidgetContent>
		</Widget>
	)
}
